import React, { useState, useEffect, useRef } from "react";
import { api, extractError } from "@/lib/api";
import { StickyNote, X } from "lucide-react";
import ClientAutocomplete from "@/components/ClientAutocomplete";

/** Fixed floating button (bottom-right) that opens a lightweight quick-note
 * modal. Lets you pick a client with autocomplete and jot a note without
 * leaving the current page. */
export default function QuickNoteButton() {
    const [open, setOpen] = useState(false);
    const [clients, setClients] = useState([]);
    const [noteTypes, setNoteTypes] = useState([]);
    const [clientId, setClientId] = useState("");
    const [content, setContent] = useState("");
    const [noteType, setNoteType] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const areaRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        Promise.all([
            api.get("/clients").catch(() => ({ data: [] })),
            api.get("/settings/note-types").catch(() => ({ data: [] })),
        ]).then(([c, t]) => {
            setClients(c.data); setNoteTypes(t.data);
            if (!noteType && t.data.length) setNoteType(t.data[0].key || t.data[0].id);
        });
        setTimeout(() => areaRef.current?.focus(), 100);
    }, [open]); // eslint-disable-line

    const reset = () => { setClientId(""); setContent(""); setErr(""); };
    const close = () => { setOpen(false); reset(); };

    const submit = async (e) => {
        e.preventDefault();
        if (!clientId) { setErr("Seleziona un cliente"); return; }
        if (!content.trim()) { setErr("Scrivi una nota"); return; }
        setBusy(true); setErr("");
        try {
            await api.post(`/clients/${clientId}/notes`, {
                content: content.trim(), type_key: noteType,
            });
            close();
        } catch (e) { setErr(extractError(e)); }
        finally { setBusy(false); }
    };

    return (
        <>
            <button onClick={() => setOpen(true)}
                data-testid="quick-note-btn"
                title="Nota cliente rapida"
                className="fixed bottom-6 right-6 z-40 bg-neutral-900 text-white rounded-full shadow-lg h-12 w-12 flex items-center justify-center hover:bg-neutral-800 transition-colors">
                <StickyNote size={18} />
            </button>
            {open && (
                <div className="fixed inset-0 bg-neutral-900/50 z-50 flex items-center justify-center p-4" onClick={close}>
                    <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
                        data-testid="quick-note-modal"
                        className="bg-white rounded-md shadow-lg w-full max-w-md">
                        <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-200">
                            <div className="font-display font-semibold">Nota cliente rapida</div>
                            <button type="button" onClick={close}><X size={16} /></button>
                        </div>
                        <div className="p-5 space-y-3 text-sm">
                            <label className="block">
                                <span className="text-xs font-medium text-neutral-600">Cliente*</span>
                                <div className="mt-1">
                                    <ClientAutocomplete clients={clients} value={clientId}
                                        onChange={setClientId}
                                        onClientCreated={(c) => { setClients([c, ...clients]); setClientId(c.id); }}
                                        testId="quick-note-client" />
                                </div>
                            </label>
                            <label className="block">
                                <span className="text-xs font-medium text-neutral-600">Tipo</span>
                                <select value={noteType} onChange={(e) => setNoteType(e.target.value)}
                                    data-testid="quick-note-type"
                                    className="mt-1 w-full px-2 py-1.5 border border-neutral-300 rounded text-sm">
                                    {noteTypes.map((t) => <option key={t.key || t.id} value={t.key || t.id}>{t.name || t.key}</option>)}
                                </select>
                            </label>
                            <label className="block">
                                <span className="text-xs font-medium text-neutral-600">Nota*</span>
                                <textarea ref={areaRef} rows={5} value={content} onChange={(e) => setContent(e.target.value)}
                                    data-testid="quick-note-content"
                                    placeholder="Scrivi qui la nota…"
                                    className="mt-1 w-full px-2 py-1.5 border border-neutral-300 rounded text-sm" />
                            </label>
                        </div>
                        {err && <div className="mx-5 mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="quick-note-error">{err}</div>}
                        <div className="px-5 py-3 border-t border-neutral-100 flex justify-end gap-2 bg-neutral-50">
                            <button type="button" onClick={close} className="px-3 py-1.5 text-sm rounded border border-neutral-300 bg-white">Annulla</button>
                            <button type="submit" disabled={busy}
                                data-testid="quick-note-save"
                                className="px-3 py-1.5 text-sm rounded bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-60">
                                {busy ? "Salvataggio…" : "Salva nota"}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </>
    );
}
